import { CommonModule } from '@angular/common';
import {
  Component,
  OnChanges,
  SimpleChanges,
  ViewChild,
  ViewEncapsulation,
  input,
  output,
  type InputSignal,
  type OutputEmitterRef,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { TranslatePipe } from '@ngx-translate/core';
import { AgGridAngular } from 'ag-grid-angular';
import type {
  ColDef,
  GridReadyEvent,
  IDatasource,
  Theme,
} from 'ag-grid-community';
import { AllCommunityModule, ModuleRegistry } from 'ag-grid-community';
import { LinkCellRendererComponent } from './link-cell-renderer/link-cell-renderer.component';

ModuleRegistry.registerModules([AllCommunityModule]);

@Component({
  selector: 'app-grid-table',
  standalone: true,
  templateUrl: './grid-table.component.html',
  styleUrl: './grid-table.component.scss',
  imports: [
    CommonModule,
    RouterModule,
    FormsModule,
    AgGridAngular,
    TranslatePipe,
  ],
  encapsulation: ViewEncapsulation.None,
})
export class GridTableComponent<RowData> implements OnChanges {
  @ViewChild(AgGridAngular) grid?: AgGridAngular;

  public data: InputSignal<RowData[]> = input<RowData[]>([]);
  public columns = input<ColDef<RowData>[]>([]);
  public detailsBaseRoute = input<string>('');
  public title = input<string>('');
  public showSearch = input<boolean>(true);
  public rowModelType = input<'infinite' | undefined>(undefined);
  public cacheBlockSize = input<number>(10);
  public maxBlocksInCache = input<number>(50);
  public datasource = input<IDatasource | null>(null);
  public rowHeight = input<number>(56);
  public gridHeight = input<number>(600);
  public autoHeight = input<boolean>(false);

  public loading = input<boolean>(false);
  public clicked: OutputEmitterRef<void> = output<void>();

  public searchChange = output<string | undefined>();
  public emitAdd = output<void>();

  theme: Theme | 'legacy' = 'legacy';
  quickFilter = '';
  protected pendingTerm = '';
  private appliedTerm = '';

  get columnDefs(): ColDef[] {
    const cols = this.columns();
    const base = this.detailsBaseRoute();

    return cols.map((col, index) => {
      const hasFixedWidth =
        'width' in col && typeof (col as ColDef).width === 'number';

      const def: ColDef = {
        ...col,
        ...(hasFixedWidth ? {} : { flex: 1 }),
      };

      if (hasFixedWidth) {
        def.resizable = false;
        def.suppressSizeToFit = true;
      }

      if (index === 0 && base && !col.cellRenderer) {
        def.cellRenderer = LinkCellRendererComponent;
        def.cellRendererParams = {
          context: {
            baseRoute: base,
          },
        };
      }
      return def;
    });
  }

  // buffer input while typing + if it’s cleared, reset immediately
  onSearchInput(e: Event): void {
    this.pendingTerm = (e.target as HTMLInputElement).value ?? '';
    const trimmed = this.pendingTerm.trim();

    if (trimmed === '' && this.appliedTerm !== '') {
      if (this.rowModelType() === 'infinite') {
        this.searchChange.emit(undefined);
        this.grid?.api.purgeInfiniteCache();
      } else {
        this.quickFilter = '';
        this.grid?.api.setGridOption('quickFilterText', '');
      }
      this.appliedTerm = '';
    }
  }

  // Sending request via Enter | search icon
  onSearchSubmit(): void {
    const term = (this.pendingTerm ?? '').trim();

    if (this.rowModelType() === 'infinite') {
      if (term !== this.appliedTerm) {
        this.searchChange.emit(term);
        this.appliedTerm = term;
        this.grid?.api.purgeInfiniteCache();
      }
    } else {
      if (term !== this.appliedTerm) {
        this.quickFilter = term;
        this.grid?.api.setGridOption('quickFilterText', term);
        this.appliedTerm = term;
      }
    }
  }

  onGridReady(e: GridReadyEvent) {
    if (this.rowModelType() === 'infinite') {
      const ds = this.datasource();
      if (ds) e.api.setGridOption('datasource', ds);
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (
      changes['datasource'] &&
      !changes['datasource'].firstChange &&
      this.rowModelType() === 'infinite'
    ) {
      const ds = this.datasource();
      if (ds) this.grid?.api.setGridOption('datasource', ds);
    }
  }

  addElement() {
    this.emitAdd.emit();
  }
}
